import AudioMock from './common/AudioMock';
import isUsingAudioMock from './common/isUsingAudioMock';

function sendMessage(refObject, type, value) {
  if (refObject.$iframe && refObject.$iframe.contentWindow) {
    refObject.$iframe.contentWindow.postMessage({
      type,
      value,
    }, '*');
  } else refObject.dispatch(`$${type}`, value);
}

export default class {
  constructor(elem) {
    this.elem = elem;
    this.readyState = 'waiting';
    this.$iframe = null;
    this.$audio = null;
    this.$isInit = false;
    this.$info = null;
    this.$eventList = {};
    this.$prevJyroIndex = null;
  }

  $setInfo(info, originalInfo) {
    this.$info = info;
    this.readyState = 'loadedinfo';
    this.dispatch('loadedinfo', originalInfo);
    if (info.autoplay) this.$init();
  }

  $setMounted() {
    this.readyState = 'mounted';
    this.dispatch('mounted', null);
  }

  $setIframe(iframe) {
    if (this.readyState !== 'mounted') this.$setMounted(); // autoplay or play before lazyloaded
    this.$iframe = iframe;
    this.readyState = 'loading';
    this.dispatch('loadstart', null);
  }

  $iframeDidMount() {
    this.readyState = 'loaded';
    this.dispatch('loaded', null);
    this.$audio.play();
    this.requestFullscreen();
  }

  $swipeByEuler(euler) {
    if (!this.$info) return;
    const y = euler.y;
    let index;
    if (y < -Math.PI / 3) {
      index = Math.floor(this.$info.srcset.length * ((y + Math.PI) / ((2 * Math.PI) / 3)));
    } else if (y > Math.PI / 3) {
      index = Math.floor(this.$info.srcset.length * ((y - (Math.PI / 3)) / ((2 * Math.PI) / 3)));
    } else {
      index = Math.floor(this.$info.srcset.length * ((y + (Math.PI / 3)) / ((2 * Math.PI) / 3)));
    }

    if (this.$prevJyroIndex !== null) {
      this.addSrcIndex(this.$prevJyroIndex - index);
      this.dispatch('jyro', index);
    }
    this.$prevJyroIndex = index;
    if (this.$info.stereoview) sendMessage(this, 'setEulerX', euler.x);
  }

  $init() {
    let audioElem;

    if (isUsingAudioMock(this.$info)) {
      audioElem = new AudioMock(this.$info.srcset[this.$info.headIndex][1]);
    } else {
      audioElem = new Audio();
      audioElem.volume = 0.5;
      audioElem.muted = this.$info.muted;
      audioElem.src = `${this.$info.src}/${this.$info.contentId}/${this.$info.srcset[this.$info.audio][0]}/audio/audio.mp3${this.$info.queryString}`;
      audioElem.load();
    }

    audioElem.addEventListener('play', () => {
      sendMessage(this, 'play', true);
      this.dispatch('play', null);
    }, false);

    audioElem.addEventListener('pause', () => {
      sendMessage(this, 'play', false);
      this.dispatch('pause', null);
    }, false);

    audioElem.addEventListener('timeupdate', (e) => {
      sendMessage(this, 'timeupdate', e.target.currentTime);
      this.dispatch('timeupdate', e.target.currentTime);
    }, false);

    audioElem.addEventListener('ended', () => {
      if (this.$info.loop) {
        this.$replay();
      } else {
        sendMessage(this, 'ended', null);
        this.dispatch('ended', null);
      }
    }, false);

    this.$audio = audioElem;
    this.$isInit = true;
  }

  addSrcIndex(value) {
    sendMessage(this, 'addSrcIndex', value);
    return this;
  }

  setSrcIndex(value) {
    sendMessage(this, 'setSrcIndex', value);
    return this;
  }

  setFilter(value) {
    sendMessage(this, 'setFilter', value);
    return this;
  }

  setPosterIndex(value) {
    sendMessage(this, 'setPosterIndex', value);
    return this;
  }

  setIconColor(value) {
    sendMessage(this, 'setIconColor', value);
    return this;
  }

  play() {
    if (this.$isInit) this.$audio.play();
    else this.$init();
    return this;
  }

  $replay() {
    this.$audio.currentTime = 0;
    this.$audio.play();
  }

  seek(value) {
    if (this.$isInit) this.$audio.currentTime = value;
    sendMessage(this, 'seek', value);
    return this;
  }

  pause() {
    if (this.$isInit) this.$audio.pause();
    return this;
  }

  getCurrentTime() {
    return this.$isInit ? this.$audio.currentTime : 0;
  }

  getMuted() {
    return this.$isInit ? this.$audio.muted : null;
  }

  setMuted(bool) {
    if (this.$isInit) {
      this.$audio.muted = bool;
      sendMessage(this, 'changeMuted', bool);
    }
    return this;
  }

  getPlaybackRate() {
    return this.$isInit ? this.$audio.playbackRate : null;
  }

  setPlaybackRate(rate) {
    if (this.$isInit) {
      this.$audio.playbackRate = rate;
      sendMessage(this, 'changePlaybackRate', rate);
    }
    return this;
  }

  requestFullscreen() {
    if (this.$info && this.$info.allowfullscreen) sendMessage(this, 'fullscreen', true);
    return this;
  }

  exitFullscreen() {
    if (this.$info && this.$info.allowfullscreen) sendMessage(this, 'fullscreen', false);
    return this;
  }

  detach() {
    if (this.$iframe) {
      this.$audio.pause();
      this.$iframe.contentWindow.postMessage({ type: 'detach' }, '*');
    }
    this.dispatch('$detach');
  }

  on(type, callback) {
    this.$eventList[type] = this.$eventList[type] || [];
    this.$eventList[type].push(callback);
    return this;
  }

  off(type, callback) {
    if (this.$eventList[type]) {
      const index = this.$eventList[type].indexOf(callback);
      if (index !== -1) this.$eventList[type].splice(index, 1);
    }

    return this;
  }

  one(type, callback) {
    const fn = (e) => {
      callback.call(this, e);
      this.off(type, fn);
    };
    this.on(type, fn);
    return this;
  }

  getInfo(callback) {
    if (this.readyState === 'waiting') this.one('loadedinfo', (e) => { callback.call(this, e.value); });
    else callback.call(this, this.$info);
    return this;
  }

  dispatch(type, value) {
    if (this.$eventList[type]) {
      this.$eventList[type].forEach((callback) => {
        callback.call(this, { type, value, target: this });
      });
    }

    return this;
  }
}



// WEBPACK FOOTER //
// ./src/RefObject.js